import context from "~/js/editor";
import { BorderResizer } from "~/js/graph/nodeSelector";
import { extensionsByGenerics } from "~/js/network/mime-type-handler";
import { CustomElementDefinition } from "~/js/nodes/node-definition";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { generate_UUID } from "~/js/utilities/UUID";
import { FileApi } from "~/solidJs/shared/api/fileApi";
import { onNextFrame, recalculateTextAreaHeight } from "~/solidJs/shared/helpers";
import { setDataAtrOnlyInTests } from "~/solidJs/shared/helpers/testHelpers";
import { getImageDimensionsFromFile } from "~/solidJs/shared/helpers/validateImages";
import Editor from "../../../editor";
import { $div } from "../../../utilities/TurboDom";

const commentFrameGlobalId = "commentFrame";
const commentFrameStickerGlobalId = "commentFrameSticker";
export type ITextTypesUnion = "title" | "subTitle" | "comment";

export type ICommentFrameDefinition = {
	globalId: string | undefined;
	id: string;
	initialSize: {
		width?: number;
		height?: number;
	};
	initialPosition?: {
		x: number;
		y: number;
	};
	initialImageUrl?: string;
} & { [key in ITextTypesUnion]?: string };

export type ICommentFrameStickerDefinition = {
	url?: string;
	fileId?: number;
} & ICommentFrameDefinition;

export type CommentFrameWorkspaceDefinition = {
	globalId: string;
	id: string;
	height: number;
	width: number;
	x: number;
	y: number;
	initialImageUrl?: string;
	fileId?: number;
} & { [key in ITextTypesUnion]?: string };

type ICommentFrameDOM = {
	sticker: HTMLDivElement;

	stickerHeader: HTMLDivElement;
	stickerHeaderTextContainer: HTMLDivElement;
	stickerBody: HTMLDivElement;
	stickerLearnMore: HTMLDivElement;

	close: HTMLDivElement;

	title: HTMLTextAreaElement;
	subTitle: HTMLTextAreaElement;
	comment: HTMLTextAreaElement;

	resizerRight: HTMLDivElement;
	resizerLeft: HTMLDivElement;
	resizerBottom: HTMLDivElement;
	resizerTop: HTMLDivElement;

	resizerCornerRight: HTMLDivElement;
	resizerCornerLeft: HTMLDivElement;
};
type ICommentFrameWithInputsDOM = {
	fileInput: HTMLInputElement;
	fileInputLabel: HTMLLabelElement;
} & ICommentFrameDOM;

export const commentFrameSideBarDefinition = new CustomElementDefinition(
	"./",
	commentFrameGlobalId,
	"Comment Frame",
	"Ability to see 3D model and apply texture.",
	null,
	null,
	(params?: CommentFrameWorkspaceDefinition) => {
		if (params?.x && params?.y) {
			const pos = Editor.context.graph.navigation.clientToGraphSpace(params.x, params.y);
			Editor.context.graph.addCommentFrame({ ...params, x: pos.x, y: pos.y });
			return;
		}
		Editor.context.graph.addCommentFrame({});
	}
);

export const commentFrameStickerSideBarDefinition = new CustomElementDefinition(
	"./",
	commentFrameStickerGlobalId,
	"Sticker",
	"Ability to see 3D model and apply texture.",
	null,
	null,
	(params?: CommentFrameWorkspaceDefinition) => {
		if (params?.x && params?.y) {
			const pos = Editor.context.graph.navigation.clientToGraphSpace(params.x, params.y);
			Editor.context.graph.addCommentFrameSticker({
				...params,
				x: pos.x,
				y: pos.y
			});
			return;
		}
		Editor.context.graph.addCommentFrameSticker({});
	}
);

export class CommentFrame extends BorderResizer {
	//config
	BORDER_SIZE_WIDTH = 6;
	BORDER_SIZE_HEIGHT = 50;
	MIN_HEIGHT = 100;
	MIN_WIDTH = 100;

	//properties
	DOM: ICommentFrameDOM = {} as ICommentFrameDOM;
	isAvailable: boolean;
	visible: boolean;
	isMoving = false;
	isMoved = false;
	imageUrl: string | undefined;
	id: string;

	startWidth = 600;
	startHeight = 900;
	startMouseX = 0;
	startMouseY = 0;
	startElX = 0;
	startElY = 0;

	title = "";
	subTitle = "";
	comment = "";

	groupedNode: Record<string, boolean>;

	constructor(definition: ICommentFrameDefinition) {
		super();
		if (typeof definition.id === "number") {
			this.id = generate_UUID();
		} else {
			this.id = definition.id;
		}
		this.groupedNode = {};
		this.initDom();

		this.DOM.close.addEventListener("click", () => {
			this.delete();
		});

		this.zoomScale = Editor.context.graph.navigation.getZoomScale();

		this.isAvailable = false;

		definition?.initialImageUrl && this.setImage(definition.initialImageUrl);
		if (!definition.initialImageUrl) {
			this.setupTextBlock();
			onNextFrame(() => {
				this.setupInitText(definition.title, definition.subTitle, definition.comment);
			});
		}
		if (definition.initialSize) {
			this.setHeight(definition.initialSize.height || this.startHeight);
			this.setWidth(definition.initialSize.width || this.startWidth);
		}

		this.setupPosition(definition.initialPosition);

		this.visible = true;
		this.initMoveAbility();
	}

	setupPosition(initialPosition: ICommentFrameDefinition["initialPosition"]) {
		if (initialPosition) {
			this.setPosition(initialPosition);
			return;
		}

		const width = this.startWidth * this.zoomScale;
		const height = this.startHeight * this.zoomScale;
		const { x, y } = context.context.graph.navigation.clientToGraphSpace(
			document.body.clientWidth / 2 - width / 2,
			document.body.clientHeight / 2 - height / 2
		);
		this.setPosition({ x, y });
	}

	setupInitText(title?: string, subTitle?: string, comment?: string) {
		this.changeTextState("title", title);
		this.changeTextState("subTitle", subTitle);
		this.changeTextState("comment", comment);
		if (title !== undefined) {
			this.DOM.title.placeholder = "";
		}
		if (subTitle !== undefined) {
			this.DOM.subTitle.placeholder = "";
		}
		if (comment !== undefined) {
			this.DOM.comment.placeholder = "";
		}
	}

	changeTextState(key: ITextTypesUnion, value?: string) {
		this[key] = value || "";
		this.DOM[key].value = value || "";
		recalculateTextAreaHeight(this.DOM[key]);
	}

	addNodeToCommentFrame(uuid: string) {
		this.groupedNode[uuid] = true;
	}

	removeFromNodeToCommentFrame(uuid: string) {
		delete this.groupedNode[uuid];
	}

	initDom() {
		this.DOM.sticker = $div("templateSticker");
		setDataAtrOnlyInTests(() =>
			this.DOM.sticker.setAttribute("data-test", `layout${commentFrameGlobalId}`)
		);
		this.DOM.sticker.style.width = "100px";
		this.DOM.sticker.style.height = "100px";

		this.DOM.resizerCornerRight = $div("resizerCornerRight resizer", this.DOM.sticker);
		this.DOM.resizerCornerLeft = $div("resizerCornerLeft resizer", this.DOM.sticker);
		this.DOM.resizerRight = $div("resizerWidth resizer", this.DOM.sticker);
		this.DOM.resizerLeft = $div("resizerLeft resizer", this.DOM.sticker);
		this.DOM.resizerBottom = $div("resizerHeight resizer", this.DOM.sticker);
		this.DOM.resizerTop = $div("resizerTop resizer", this.DOM.sticker);

		this.DOM.stickerHeader = $div("sticker-header", this.DOM.sticker);
		this.DOM.stickerHeaderTextContainer = $div(
			"sticker-header-text-container",
			this.DOM.stickerHeader
		);
		this.DOM.stickerBody = $div("sticker-body", this.DOM.sticker);
		this.DOM.stickerLearnMore = $div("sticker-learn-more", this.DOM.sticker);

		this.DOM.close = $div("close", this.DOM.stickerHeader);
		this.DOM.sticker.classList.add("activeBorder");
	}

	superKeyDownHandler(e: KeyboardEvent) {
		e.stopPropagation();
	}

	superOnInput(e: Event) {
		const target = e.target as HTMLTextAreaElement;

		return target.value;
	}

	setupTextBlock() {
		this.DOM.title = document.createElement("textarea");
		this.DOM.subTitle = document.createElement("textarea");
		this.DOM.comment = document.createElement("textarea");
		const els = [
			{ element: this.DOM.title, name: "title" },
			{ element: this.DOM.subTitle, name: "subTitle" },
			{ element: this.DOM.comment, name: "comment" }
		] as const;
		for (const el of els) {
			this.bindEventsToTextArea(el.element, el.name);
		}
		this.DOM.title.spellcheck = false;
		this.DOM.subTitle.spellcheck = false;
		this.DOM.comment.spellcheck = false;

		this.DOM.title.classList.add("title");
		this.DOM.subTitle.classList.add("subTitle");
		this.DOM.comment.classList.add("comment");

		this.DOM.title.placeholder = "TITLE";
		this.DOM.subTitle.placeholder = "SUBTITLE";
		this.DOM.comment.placeholder = "comment";

		this.DOM.stickerHeaderTextContainer.append(this.DOM.title);
		this.DOM.stickerHeaderTextContainer.append(this.DOM.subTitle);
		this.DOM.stickerHeaderTextContainer.append(this.DOM.comment);
	}

	bindEventsToTextArea(textArea: HTMLTextAreaElement, name: ITextTypesUnion) {
		textArea.addEventListener("blur", (_e) => {
			if (textArea.value) {
				return;
			}
			textArea.placeholder = "";
		});
		textArea.addEventListener("dragstart", (e) => {
			e.stopPropagation();
			e.preventDefault();
		});
		textArea.addEventListener("input", (e) => {
			const value = this.superOnInput(e);
			this.changeTextState(name, value);
		});
		textArea.addEventListener("keydown", this.superKeyDownHandler);
	}

	initDomMove() {
		this.domElement().addEventListener("mousedown", (event) => {
			// Don't handle mousedown if Alt is pressed (user is copying nodes inside)
			if (event.altKey) {
				return;
			}

			const mouseUpHandler = () => {
				document.removeEventListener("mouseup", mouseUpHandler);
				if (this.isMoved) {
					this.isMoved = false;
					return;
				}
				if (event.shiftKey) {
					Editor.context.graph.removeCommentFrameFromSelect(this);
				} else {
					Editor.context.graph.singleSelectCommentFrame(this);
				}
			};
			event.stopPropagation();
			this.startWidth = this.domElement().offsetWidth;
			this.startHeight = this.domElement().offsetHeight;
			if (Editor.context.graph.isCommentFrameSelected(this)) {
				Editor.context.graph.moveAllSelectedNodes(event);
				document.addEventListener("mouseup", mouseUpHandler);
			} else {
				if (event.shiftKey) {
					Editor.context.graph.addCommentFrameToSelect(this);
				} else {
					Editor.context.graph.singleSelectCommentFrame(this);
					Editor.context.graph.moveAllSelectedNodes(event);
					document.addEventListener("mouseup", mouseUpHandler);
				}
			}
		});
	}

	domElement(): HTMLDivElement {
		return this.DOM.sticker;
	}

	serializeCommentFrame() {
		return {
			id: this.id,
			url: this.imageUrl,
			...this.getPosition(),
			...this.getSize(),
			title: this.title,
			subTitle: this.subTitle,
			comment: this.comment,
			globalId: commentFrameGlobalId
		};
	}

	postResizeMoveCallback() {
		for (const node of Editor.context.graph.nodes) {
			Editor.context.graph.isNodeRelatedToCommentFrame(node, this);
		}
	}

	superMouseDownHandler(event: MouseEvent, callBackToMouseMove: (e: MouseEvent) => void) {
		event.stopPropagation();

		this.zoomScale = Editor.context.graph.navigation.getZoomScale();

		this.startWidth = this.domElement().offsetWidth;
		this.startHeight = this.domElement().offsetHeight;

		this.startMouseX = event.clientX;
		this.startMouseY = event.clientY;

		const { x, y } = this.getPosition();

		this.startElX = x;
		this.startElY = y;
		document.addEventListener("mousemove", callBackToMouseMove, false);

		const mouseUp = () => {
			this.postResizeMoveCallback();
			document.removeEventListener("mousemove", callBackToMouseMove, false);
			document.removeEventListener("mouseup", mouseUp, false);
		};
		document.addEventListener("mouseup", mouseUp);
	}

	initMoveAbility() {
		this.DOM.resizerBottom.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeBottomHeight.bind(this));
		});
		this.DOM.resizerLeft.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeLeftWidth.bind(this));
		});
		this.DOM.resizerTop.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeTopHeight.bind(this));
		});
		this.DOM.resizerRight.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeRightWidth.bind(this));
		});
		this.DOM.resizerCornerRight.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeBottomRightCorner.bind(this));
		});
		this.DOM.resizerCornerLeft.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeBottomLeftCorner.bind(this));
		});
		this.initDomMove();
	}

	moveSticker(startX: number, startY: number) {
		if (this.isMoving) {
			return;
		}
		this.isMoving = true;
		const zoomScale = Editor.context.graph.navigation.getZoomScale();
		const x = this.domElement().offsetLeft - startX / zoomScale;
		const y = this.domElement().offsetTop - startY / zoomScale;

		this.moveRelatedNodes(startX, startY);

		const mouseMoveHandler = (e: MouseEvent) => {
			this.isMoved = true;
			this.setPosition({
				x: e.clientX / zoomScale + x,
				y: e.clientY / zoomScale + y
			});
		};

		const mouseUpHandler = () => {
			this.isMoving = false;
			this.postResizeMoveCallback();
			document.removeEventListener("mousemove", mouseMoveHandler);
			document.removeEventListener("mouseup", mouseUpHandler);
		};

		document.addEventListener("mousemove", mouseMoveHandler);
		document.addEventListener("mouseup", mouseUpHandler);
	}

	select(force: boolean) {
		this.domElement().classList.toggle("selected", force);
	}

	moveRelatedNodes(startX: number, startY: number) {
		for (const nodeUUid in this.groupedNode) {
			const nodeInGraph = Editor.context.graph.nodesHashMap.get(nodeUUid);
			if (!nodeInGraph) {
				delete this.groupedNode[nodeUUid];
				continue;
			}
			Editor.context.graph.moveNode(nodeInGraph, startX, startY);
		}
	}

	domBodyElement() {
		return this.DOM.stickerBody;
	}

	setImage(imageUrl: string) {
		if (!imageUrl || imageUrl === undefined || imageUrl === null) {
			return false;
		}
		this.imageUrl = imageUrl;

		const img = document.createElement("img");
		img.src = imageUrl;
		img.classList.add("sticker-image");
		img.setAttribute("crossorigin", "anonymous");
		this.DOM.stickerBody.appendChild(img);
	}

	hide() {
		this.domElement().style.display = "none";
		this.visible = false;
	}

	show() {
		if (this.imageUrl) {
			this.domElement().style.display = "block";
			this.visible = true;
		}
	}

	delete() {
		Editor.context?.graph?.deleteCommentFrame({ id: this.id });
	}

	removeFromDom() {
		this.DOM.sticker.remove();
	}
}

export class CommentFrameSticker extends CommentFrame {
	isMoving = false;
	declare DOM: ICommentFrameWithInputsDOM;
	fileId: number | undefined;

	constructor(definition: ICommentFrameStickerDefinition) {
		super(definition);

		if (definition.fileId) {
			this.fileId = definition.fileId;
			this.loadFileFromId();
		}
	}
	setupTextBlock() {}
	setupInitText() {}
	initDom() {
		this.DOM.sticker = $div("templateSticker image");
		setDataAtrOnlyInTests(() =>
			this.DOM.sticker.setAttribute("data-test", `layout${commentFrameGlobalId}`)
		);
		this.DOM.sticker.style.width = "100px";
		this.DOM.sticker.style.height = "100px";

		this.DOM.resizerCornerRight = $div("resizerCornerRight resizer", this.DOM.sticker);
		this.DOM.resizerCornerLeft = $div("resizerCornerLeft resizer", this.DOM.sticker);

		this.DOM.stickerHeader = $div("sticker-header", this.DOM.sticker);
		this.DOM.stickerBody = $div("sticker-body", this.DOM.sticker);
		this.DOM.close = $div("close", this.DOM.stickerHeader);

		const inputFile = document.createElement("input");
		inputFile.multiple = false;
		inputFile.type = "file";
		inputFile.hidden = true;
		inputFile.id = `sticker-file${this.id}`;
		inputFile.accept = extensionsByGenerics.image.join(", ");
		const inputFileLabel = document.createElement("label");
		inputFileLabel.htmlFor = `sticker-file${this.id}`;
		inputFileLabel.textContent = "Upload files";
		inputFileLabel.setAttribute("data-test", "openFileBtn");
		inputFileLabel.classList.add("read-file-input");
		this.DOM.fileInput = inputFile;
		this.DOM.fileInputLabel = inputFileLabel;
		this.DOM.stickerBody.append(inputFileLabel, inputFile);
		this.initOnFileChange();
	}

	hideInput(force: boolean) {
		this.DOM.fileInputLabel!.classList.toggle("hidden", force);
	}

	initOnFileChange() {
		this.DOM.fileInput!.addEventListener("change", async (e: Event) => {
			const files = (e.target as HTMLInputElement).files;
			if (!files?.length) return;
			const file = files[0];
			const fileId = await FileApi.pushFileAsync(file);
			this.fileId = fileId;
			this.loadFileFromId();
			const size = await getImageDimensionsFromFile(file);
			if (!size) return;
			this.setWidth(size.width);
			this.setHeight(size.height);
		});
	}

	select(force: boolean) {
		this.domElement().classList.toggle("selected", force);
	}

	async loadFileFromId() {
		if (!this.fileId) return;
		const link = await FileApi.getLinkToAwsByFileId(this.fileId);
		this.setImage(link.download_link);
		this.hideInput(true);
	}

	domElement(): HTMLDivElement {
		return this.DOM.sticker;
	}

	override serializeCommentFrame() {
		return {
			url: this.imageUrl,
			fileId: this.fileId,
			globalId: commentFrameStickerGlobalId,
			...this.getPosition(),
			...this.getSize(),
			type: "commentFrame",
			subTitle: "",
			title: "",
			comment: "",
			id: ""
		};
	}

	initMoveAbility() {
		this.DOM.resizerCornerRight.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeBottomRightCorner.bind(this));
		});
		this.DOM.resizerCornerLeft.addEventListener("mousedown", (event) => {
			this.superMouseDownHandler(event, this.resizeBottomLeftCorner.bind(this));
		});
		super.initDomMove();
	}

	setImage(imageUrl: string) {
		if (!imageUrl || imageUrl === undefined || imageUrl === null) {
			return false;
		}
		this.imageUrl = imageUrl;

		const img = document.createElement("img");
		img.src = imageUrl;
		img.classList.add("sticker-image");
		img.setAttribute("crossorigin", "anonymous");
		this.DOM.stickerBody.appendChild(img);
	}

	hide() {
		this.domElement().style.display = "none";
		this.visible = false;
	}

	show() {
		if (this.imageUrl) {
			this.domElement().style.display = "block";
			this.visible = true;
		}
	}

	delete() {
		EventAnalytics.deleteSticker(this.imageUrl);
		Editor.context?.graph?.deleteCommentFrame({ id: this.id });
	}

	removeFromDom() {
		this.DOM.sticker.remove();
	}
	addNodeToCommentFrame() {}
	removeFromNodeToCommentFrame() {}
}

export const loadCommentFrameAccordType = (props: CommentFrameWorkspaceDefinition) => {
	if (props.globalId === commentFrameStickerGlobalId) {
		Editor.context.graph.addCommentFrameSticker(props);
		return;
	}
	return Editor.context.graph.addCommentFrame(props);
};

export const loadCommentFramesAccordType = (props: CommentFrameWorkspaceDefinition[]) => {
	for (const prop of props) {
		loadCommentFrameAccordType(prop);
	}
};
